"use client";

import { motion } from "framer-motion";
import { ArrowDown, Sparkles, Star, Leaf, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";

const stats = [
  { value: "04", label: "Racines d'excellence" },
  { value: "12+", label: "Produits transformés" },
  { value: "100%", label: "Vente directe" },
];

export function Hero() {
  const scrollTo = (href: string) => {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="accueil"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* ===== Full-bleed background image ===== */}
      <div className="absolute inset-0">
        <img
          src="/images/hero-bg.png"
          alt="Récolte dorée de patates, ignames, manioc et pommes de terre"
          className="w-full h-full object-cover ken-burns"
          onError={(e) => {
            const t = e.currentTarget as HTMLImageElement;
            t.src = "/images/hero-harvest.png";
          }}
        />
      </div>

      {/* Subtle gradient overlays — image stays dominant */}
      <div className="absolute inset-0 bg-gradient-to-r from-earth-deep/85 via-earth-deep/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-earth-deep/95 via-transparent to-earth-deep/40" />

      {/* Floating decorative gold orbs */}
      <motion.div
        aria-hidden
        className="absolute -top-20 -left-20 w-80 h-80 rounded-full blur-3xl opacity-60"
        style={{ background: "radial-gradient(circle, oklch(0.86 0.13 80 / 0.4), transparent 70%)" }}
        animate={{ y: [0, 30, 0], x: [0, 20, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-50"
        style={{ background: "radial-gradient(circle, oklch(0.55 0.13 135 / 0.35), transparent 70%)" }}
        animate={{ y: [0, -25, 0], x: [0, -15, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* ===== Content ===== */}
      <div className="relative container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-32 pb-20 lg:pt-28">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Left content */}
          <div className="lg:col-span-7 text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card text-cream/95 text-xs sm:text-sm font-medium mb-6"
            >
              <Sparkles className="w-4 h-4 text-gold" />
              <span className="tracking-wide">
                Ndiamsyl Peye · Smartest Capital — La référence des racines d&apos;exception
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-extrabold leading-[0.92] tracking-tight"
            >
              <span className="block text-cream drop-shadow-[0_4px_20px_rgba(0,0,0,0.5)]">PATATE</span>
              <span className="block text-gradient-gold text-shadow-gold">OR</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-6 max-w-xl mx-auto lg:mx-0 text-base sm:text-lg text-cream/90 leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]"
            >
              Le plus savant, le plus joli, le plus animé, le plus ouvert, le plus
              attirant, le plus élégant, le plus nourricier, le plus patriote et le plus
              émerveillant des marchés. Patate douce, pomme de terre, manioc, igname et
              produits transformés — vendus directement du producteur à votre table.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-3"
            >
              <Button
                onClick={() => scrollTo("#produits")}
                size="lg"
                className="bg-gradient-gold text-white shadow-gold-lg hover:scale-[1.04] transition-all font-semibold rounded-full px-7 border border-cream/20"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Découvrir nos racines
              </Button>
              <Button
                onClick={() => scrollTo("#contact")}
                size="lg"
                variant="outline"
                className="rounded-full bg-cream/10 backdrop-blur-md border-cream/40 text-cream hover:bg-cream/20 hover:text-cream px-7"
              >
                Commander en direct
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-12 grid grid-cols-3 gap-4 sm:gap-8 max-w-md mx-auto lg:mx-0"
            >
              {stats.map((s, i) => (
                <div key={i} className="text-center lg:text-left">
                  <div className="font-display text-3xl sm:text-4xl font-extrabold text-gradient-gold drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
                    {s.value}
                  </div>
                  <div className="mt-1 text-[11px] sm:text-xs uppercase tracking-wider text-cream/80 drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]">
                    {s.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right decorative card — image of producer */}
          <div className="lg:col-span-5 hidden lg:flex justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="relative"
            >
              {/* Decorative gold seal */}
              <div className="absolute -top-10 -right-10 w-32 h-32 spin-slow opacity-90 z-20">
                <svg viewBox="0 0 200 200" className="w-full h-full">
                  <defs>
                    <path
                      id="circle-text"
                      d="M 100, 100 m -75, 0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0"
                      fill="none"
                    />
                  </defs>
                  <text fill="oklch(0.86 0.13 80)" fontSize="13" fontWeight="700" letterSpacing="3">
                    <textPath href="#circle-text">
                      PATATE OR · RACINES D&apos;EXCEPTION · DEPUIS TOUJOURS ·
                    </textPath>
                  </text>
                </svg>
              </div>

              <div className="relative w-80 h-[28rem] rounded-[2rem] overflow-hidden shadow-gold-lg ring-2 ring-gold/40 float-soft">
                <img
                  src="/images/hero-producer.png"
                  alt="Productrice fière de sa récolte de patates douces et ignames"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-earth-deep/85 via-earth-deep/15 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 glass-card rounded-2xl p-4">
                  <div className="flex items-center gap-2 text-gold">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                  <p className="mt-2 text-sm text-foreground/85 font-medium italic">
                    « La racine qui nourrit le corps et émerveille l&apos;âme. »
                  </p>
                </div>
              </div>

              <motion.div
                aria-hidden
                className="absolute -bottom-6 -left-8 w-24 h-24 rounded-2xl bg-leaf/90 backdrop-blur grid place-items-center shadow-gold rotate-[-8deg]"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 }}
              >
                <Leaf className="w-10 h-10 text-white" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={() => scrollTo("#histoire")}
        aria-label="Défiler vers le bas"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ delay: 1.2, duration: 2, repeat: Infinity }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 grid place-items-center w-11 h-11 rounded-full bg-cream/15 backdrop-blur-md border border-cream/30 text-cream/90 hover:bg-cream/25 hover:text-cream transition-colors z-10"
      >
        <ArrowDown className="w-4 h-4" />
      </motion.button>
    </section>
  );
}
